import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Nav } from 'react-bootstrap';
import { taskAPI } from '../../services';

/**
 * #200056 — bảng chọn lại ảnh/video mẫu đã dùng ở task trước, mang theo nguyên tiêu chí.
 *
 * #181106 — thêm TAB "Kho ảnh": gộp ảnh mẫu đã dùng với ảnh NHÂN VIÊN đã tải lên làm bằng chứng,
 * để người cấu hình task chọn được ảnh thật nhân viên đã up (không chỉ ảnh mẫu cũ).
 *
 * #205306 — thêm chế độ `mode="replace"`: THAY ảnh của MẪU đang có bằng ảnh NHÂN VIÊN đã chụp cho
 * ĐÚNG việc đang sửa (`task_id`), GIỮ NGUYÊN tiêu chí QC của mẫu. Chế độ này không hiện tab và
 * không kèm ảnh mẫu (ảnh mẫu không gắn theo việc).
 *
 * Đặt trong màn QUẢN LÝ (Cấu hình Task), đúng chỗ sếp chốt. Danh sách ảnh cũ phình to theo thời
 * gian nên cố ý KHÔNG tải hết 1 lần: mỗi lượt 12 ảnh, có ô tìm kiếm + nút "Xem thêm".
 * Ảnh mẫu gắn theo CHI NHÁNH, nên bảng có ô chọn chi nhánh (mặc định = chi nhánh của task đang sửa).
 */

const MOI_TRANG = 12;

const laVideo = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url || '');

const KHO_ANH = {
  used: {
    nhan: 'Ảnh đã dùng trước',
    trong: 'Chi nhánh này chưa có ảnh mẫu nào đã dùng trước.',
    khongKhop: 'Không tìm thấy ảnh mẫu nào khớp.',
    loi: 'Không tải được danh sách ảnh đã dùng',
    testId: 'tc-anh-cu',
  },
  library: {
    nhan: 'Kho ảnh',
    trong: 'Chi nhánh này chưa có ảnh nào trong kho.',
    khongKhop: 'Không tìm thấy ảnh nào khớp.',
    loi: 'Không tải được kho ảnh',
    testId: 'tc-kho-anh',
  },
  // #205306 — chế độ THAY ảnh mẫu: chỉ ảnh NHÂN VIÊN đã chụp cho ĐÚNG việc đang sửa.
  employee: {
    nhan: 'Ảnh nhân viên đã chụp',
    trong: 'Việc này chưa có ảnh nhân viên nào đã chụp.',
    khongKhop: 'Không tìm thấy ảnh nhân viên nào khớp.',
    loi: 'Không tải được ảnh nhân viên đã chụp',
    testId: 'tc-thay-anh',
  },
};

export default function SampleEvidencePickerModal({
  show,
  onHide,
  branches = [],
  defaultBranchId,
  existingUrls = [],
  onPick,
  // #205306 — 'add' (mặc định, thêm mẫu mới) hoặc 'replace' (thay ảnh của mẫu đang có).
  mode = 'add',
  taskId = null,
}) {
  const laThay = mode === 'replace';
  const [tab, setTab] = useState('used');
  const [branchId, setBranchId] = useState('');
  const [tuKhoa, setTuKhoa] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mở bảng thì bắt đầu lại: chi nhánh mặc định = chi nhánh task (hoặc chi nhánh đầu tiên), xoá từ khoá.
  useEffect(() => {
    if (!show) return;
    setBranchId(defaultBranchId || branches?.[0]?.id || '');
    setTuKhoa('');
    setTab(laThay ? 'library' : 'used');
  }, [show, defaultBranchId, branches, laThay]);

  const load = useCallback(
    async (pageCanTai, q, noiTiep) => {
      if (!branchId) {
        setItems([]);
        setTotal(0);
        setHasMore(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const fetcher =
          laThay || tab === 'library'
            ? taskAPI.getEvidenceLibrary
            : taskAPI.getSampleEvidenceSuggestions;
        const res = await fetcher({
          branch_id: branchId,
          // #205306 — chế độ thay ảnh chỉ lấy ảnh nhân viên của ĐÚNG việc đang sửa.
          task_id: laThay ? taskId : undefined,
          q: q.trim() || undefined,
          page: pageCanTai,
          limit: MOI_TRANG,
        });
        const data = Array.isArray(res?.data) ? res.data : [];
        setItems((cu) => (noiTiep ? [...cu, ...data] : data));
        setPage(pageCanTai);
        setHasMore(Boolean(res?.has_more));
        setTotal(typeof res?.total === 'number' ? res.total : data.length);
      } catch (err) {
        setError(err?.message ?? KHO_ANH[tab].loi);
        if (!noiTiep) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [branchId, tab, laThay, taskId],
  );

  // Một chỗ tải duy nhất cho cả lúc mở bảng, lúc đổi tab lẫn lúc gõ tìm kiếm; chờ 250ms cho hết nhịp gõ.
  useEffect(() => {
    if (!show) return undefined;
    const id = setTimeout(() => load(1, tuKhoa, false), 250);
    return () => clearTimeout(id);
  }, [show, tuKhoa, branchId, tab, load]);

  const chonTab = (khoa) => {
    if (khoa === tab) return;
    setTab(khoa);
    setItems([]);
    setTotal(0);
    setHasMore(false);
    setError(null);
  };

  const chu = laThay ? KHO_ANH.employee : KHO_ANH[tab];

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {laThay ? 'Thay ảnh mẫu' : 'Chọn ảnh cho mẫu bằng chứng'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {laThay ? (
          <p className="text-muted small mb-2">
            Chọn 1 ảnh nhân viên đã chụp cho việc này để thay cho ảnh mẫu hiện tại. Tiêu chí QC
            của mẫu được giữ nguyên.
          </p>
        ) : (
          <Nav
            variant="tabs"
            activeKey={tab}
            onSelect={chonTab}
            className="mb-3"
            data-testid="tc-chon-anh-tab"
          >
            <Nav.Item>
              <Nav.Link eventKey="used" data-testid="tc-tab-anh-cu">
                {KHO_ANH.used.nhan}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="library" data-testid="tc-tab-kho-anh">
                {KHO_ANH.library.nhan}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        )}

        <Form.Group className="mb-2">
          <Form.Label className="small text-muted mb-1">Chi nhánh</Form.Label>
          <Form.Select
            size="sm"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="tc-anh-cu-chi-nhanh"
          >
            <option value="">— Chọn chi nhánh —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Control
            size="sm"
            type="text"
            placeholder={laThay ? 'Tìm theo đường dẫn ảnh...' : 'Tìm theo tên task hoặc tiêu chí...'}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
            style={{ fontSize: 16 }}
            data-testid="tc-anh-cu-tim"
          />
        </Form.Group>

        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {!branchId && !error && (
          <p className="text-center text-muted small py-4" data-testid="tc-anh-cu-chua-chon-cn">
            Chọn chi nhánh để xem ảnh.
          </p>
        )}

        {branchId && !loading && items.length === 0 && !error && (
          <p className="text-center text-muted small py-4" data-testid={`${chu.testId}-trong`}>
            {tuKhoa.trim() ? chu.khongKhop : chu.trong}
          </p>
        )}

        <div className="d-flex flex-column gap-2">
          {items.map((item) => {
            // #205306 — chế độ thay ảnh không chặn ảnh đang dùng (chọn lại cũng vô hại).
            const daCo = !laThay && existingUrls.includes(item.url);
            const preview = item.criteria || [];
            const laNhanVien = item.source === 'employee';
            return (
              <div
                key={item.url}
                className="d-flex align-items-center gap-2 border rounded p-2"
                data-testid={`${chu.testId}-muc`}
              >
                <div
                  className="d-flex align-items-center justify-content-center border rounded bg-light"
                  style={{ width: 44, height: 56, flexShrink: 0, overflow: 'hidden' }}
                >
                  {laVideo(item.url) ? (
                    <span style={{ fontSize: 11 }} className="text-muted">
                      Video
                    </span>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.task_name || 'Ảnh trong kho'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </div>

                <div className="flex-grow-1 min-w-0">
                  <p className="mb-0 text-truncate" style={{ fontSize: 13, fontWeight: 500 }} title={item.task_name}>
                    {item.task_name || '(task không tên)'}
                  </p>
                  <p className="mb-0 text-truncate text-muted" style={{ fontSize: 11 }}>
                    {laNhanVien
                      ? 'Ảnh nhân viên đã tải lên'
                      : item.criteria_count > 0
                        ? `${item.criteria_count} tiêu chí: ${preview.map((c) => c.name).join(' · ')}`
                        : 'Chưa có tiêu chí'}
                  </p>
                </div>

                <Button
                  type="button"
                  size="sm"
                  variant={daCo ? 'outline-secondary' : 'outline-primary'}
                  className="flex-shrink-0"
                  style={{ fontSize: 12 }}
                  disabled={daCo}
                  onClick={() => onPick(item)}
                  data-testid={`${chu.testId}-chon-${item.url}`}
                >
                  {laThay ? 'Thay ảnh' : daCo ? 'Đã thêm' : 'Chọn'}
                </Button>
              </div>
            );
          })}
        </div>

        {loading && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {hasMore && !loading && (
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="w-100 mt-3"
            onClick={() => load(page + 1, tuKhoa, true)}
            data-testid={`${chu.testId}-xem-them`}
          >
            Xem thêm
          </Button>
        )}

        {items.length > 0 && (
          <p className="text-center text-muted mb-0 mt-2" style={{ fontSize: 11 }}>
            Đang hiện {items.length}/{total} ảnh
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}
